/**
 * QuestionListPanel — presentational left panel of `QuestionsPage`.
 *
 * State (filters, selection, tags, data fetch) is owned by `QuestionsPage`
 * and passed in via props. This file is responsible only for rendering:
 *   - list header (title + count + slot for header actions)
 *   - status bar
 *   - filter rows (subject/module/type/exam/grade/...)
 *   - advanced-filter accordion
 *   - tag pills
 *   - multi-select bar
 *   - the list rows
 *
 * Extracted from `QuestionsPage.tsx` during the 2026-05-17 split-pane refactor.
 */
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { LatexText } from '../latex';
import { buildQuestionDisplayText } from '@padcanvas/shared';
import { MultiSelectFilter, DatePicker } from '../components';
import {
    PROGRAMME_OPTIONS,
    QUESTION_TYPES,
    QUESTION_TYPE_LABELS,
    GRADE_OPTIONS,
    type QuestionType,
} from '@padcanvas/shared';
import { READINESS_LABELS, ENRICH_STATUS_LABELS, DIFFICULTY_OPTIONS } from '../question-set-types';
import { TopicSelector } from '../components/TopicSelector';
import type { QuestionListItem } from './types';
import type { ModuleItem } from '../reference-data/types';
import {
    DIAGRAM_LOCATIONS,
    REJECTED_EXCLUSIVE,
    type QuestionFilters,
} from './filterTypes';
import {
    getDifficultyColor,
    getReadinessColor,
    getReadinessLabel,
    getExamBadgeText,
} from './helpers';

const QUESTION_TYPE_OPTIONS = QUESTION_TYPES;

export interface QuestionListPanelProps {
    // Header / status
    totalCount: number | undefined;
    filteredCount: number;
    renderHeaderActions?: () => ReactNode;
    renderAboveFilters?: () => ReactNode;
    statusMessage?: { type: 'success' | 'error'; message: string } | null;
    onClearStatus?: () => void;
    onStatusClick?: () => void;

    // Filter state
    filters: QuestionFilters;
    setFilter: <K extends keyof QuestionFilters>(key: K, value: QuestionFilters[K]) => void;
    subjectOptions: ReadonlyArray<string>;
    modulesBySubject: Record<string, ModuleItem[]>;
    isDirty: boolean;
    isFetching: boolean;
    handleFetch: () => void;
    isAdmin: boolean;
    showStatusFilter?: boolean;
    /** Drop the filters that only mean something over the shared catalogue —
     *  target exam, class, and the whole advanced block. A reader looking at
     *  their OWN questions has a handful of rows from one or two papers they
     *  uploaded themselves: narrowing them by exam board or by which shift of
     *  which year the question appeared in is a control that can only return
     *  everything or nothing. Subject, topic, type, difficulty and search stay,
     *  because those do separate a handful of rows. */
    hideCatalogueFilters?: boolean;

    // Advanced-filter accordion
    advancedOpen: boolean;
    setAdvancedOpen: Dispatch<SetStateAction<boolean>>;
    selectedDiagramLocations: Set<string>;
    toggleDiagramLocation: (loc: string) => void;
    advancedFilterCount: number;
    onClearAdvancedFilters: () => void;

    // Tags
    allTagNames: string[];
    selectedTags: Set<string>;
    setSelectedTags: Dispatch<SetStateAction<Set<string>>>;
    tagQuestionSets: Map<string, Set<string>>;
    questionTagNames: Map<string, string[]>;

    // Selection / list rows
    selectedQuestionId: string | null;
    onSelectQuestion: (id: string) => void;
    selectedIds: Set<string>;
    toggleSelectQuestion: (id: string) => void;
    toggleSelectAll: () => void;
    renderSelectionBar?: (selectedIds: Set<string>, onClear: () => void) => ReactNode;
    clearSelection: () => void;

    // Data
    isLoading: boolean;
    filteredQuestions: QuestionListItem[];
    modulesById: Record<string, ModuleItem>;
}

export function QuestionListPanel({
    totalCount,
    filteredCount,
    renderHeaderActions,
    renderAboveFilters,
    statusMessage,
    onClearStatus,
    onStatusClick,
    filters,
    setFilter,
    subjectOptions,
    modulesBySubject,
    isDirty,
    isFetching,
    handleFetch,
    isAdmin,
    showStatusFilter,
    hideCatalogueFilters,
    advancedOpen,
    setAdvancedOpen,
    selectedDiagramLocations,
    toggleDiagramLocation,
    advancedFilterCount,
    onClearAdvancedFilters,
    allTagNames,
    selectedTags,
    setSelectedTags,
    tagQuestionSets,
    questionTagNames,
    selectedQuestionId,
    onSelectQuestion,
    selectedIds,
    toggleSelectQuestion,
    toggleSelectAll,
    renderSelectionBar,
    clearSelection,
    isLoading,
    filteredQuestions,
    modulesById,
}: QuestionListPanelProps) {
    return (
        <div className="list-panel">
            <div className="list-header">
                <div className="list-title">
                    <h1>Questions</h1>
                    {totalCount !== undefined && (
                        <span className="count-badge">{filteredCount} / {totalCount}</span>
                    )}
                </div>
                {renderHeaderActions && (
                    <div className="list-actions">{renderHeaderActions()}</div>
                )}
            </div>

            {renderAboveFilters?.()}

            {statusMessage && (
                <div
                    className={`status-bar ${statusMessage.type}`}
                    onClick={onStatusClick}
                    style={onStatusClick ? { cursor: 'pointer' } : undefined}
                >
                    {statusMessage.message}
                    {onClearStatus && (
                        <button onClick={(e) => { e.stopPropagation(); onClearStatus(); }}>&times;</button>
                    )}
                </div>
            )}

            {/* Filters */}
            <div className="filters-section">
                {/* Row 1: Subject, Target Exam, Class */}
                <div className="filter-row">
                    <select
                        value={filters.subject}
                        onChange={e => setFilter('subject', e.target.value)}
                        className="filter-select"
                    >
                        {subjectOptions.map(s => (
                            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                    </select>

                    {!hideCatalogueFilters && (
                        <MultiSelectFilter
                            label="All Exams"
                            size="compact"
                            options={PROGRAMME_OPTIONS.map(exam => ({ value: exam, label: exam }))}
                            selected={filters.targetExam ? filters.targetExam.split(',') : []}
                            onChange={vals => setFilter('targetExam', vals.join(','))}
                        />
                    )}

                    {!hideCatalogueFilters && (
                        <select
                            value={filters.grade}
                            onChange={e => setFilter('grade', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Classes</option>
                            {GRADE_OPTIONS.map(c => (
                                <option key={c} value={c}>{`Class ${c}`}</option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Row 2: Module / Topic — shared searchable tree (module → chapter → topic) */}
                <div className="filter-row">
                    <TopicSelector
                        modules={filters.subject ? (modulesBySubject[filters.subject] ?? []) : []}
                        value={filters.moduleId}
                        onChange={v => setFilter('moduleId', v)}
                        placeholder={filters.subject ? 'All modules / chapters / topics' : 'Select a subject first'}
                        disabled={!filters.subject}
                        className="filter-select-wide"
                    />
                </div>

                {/* Row 3: Type, Difficulty, Status */}
                <div className="filter-row">
                    <select
                        value={filters.questionType}
                        onChange={e => setFilter('questionType', e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Types</option>
                        {QUESTION_TYPE_OPTIONS.map(t => (
                            <option key={t} value={t}>{QUESTION_TYPE_LABELS[t]}</option>
                        ))}
                    </select>

                    <select
                        value={filters.difficulty}
                        onChange={e => setFilter('difficulty', e.target.value)}
                        className="filter-select"
                    >
                        <option value="">All Difficulties</option>
                        {DIFFICULTY_OPTIONS.map(d => (
                            <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                        ))}
                    </select>

                    {(isAdmin || showStatusFilter) && (
                        <select
                            value={filters.importStatus}
                            onChange={e => setFilter('importStatus', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Status</option>
                            <option value="draft">Draft</option>
                            <option value="live">Live</option>
                        </select>
                    )}

                    {isAdmin && (
                        <select
                            value={filters.sourceType}
                            onChange={e => setFilter('sourceType', e.target.value)}
                            className="filter-select"
                        >
                            <option value="">All Sources</option>
                            <option value="previous-year-paper">Previous-year paper</option>
                            <option value="textbook">Textbook</option>
                            <option value="ai-generated">AI-generated</option>
                            <option value="online">Online</option>
                            <option value="manual">Manual</option>
                            <option value="unknown">Unknown</option>
                        </select>
                    )}
                </div>

                {/* Advanced filters (collapsible) */}
                {!hideCatalogueFilters && (
                <div className="advanced-filters">
                    <div className="advanced-filters-header">
                        <button
                            type="button"
                            className="advanced-filters-toggle"
                            onClick={() => setAdvancedOpen(o => !o)}
                            aria-expanded={advancedOpen}
                        >
                            <span className="advanced-filters-chevron">{advancedOpen ? '▾' : '▸'}</span>
                            Advanced filters
                            {advancedFilterCount > 0 && (
                                <span className="advanced-filters-count">{advancedFilterCount}</span>
                            )}
                        </button>
                        {advancedFilterCount > 0 && (
                            <button
                                type="button"
                                className="advanced-filters-clear"
                                onClick={onClearAdvancedFilters}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {advancedOpen && (
                        <div className="advanced-filters-body">
                            <div className="advanced-filter-group">
                                <span className="advanced-filter-label">Has diagram in:</span>
                                <div className="diagram-pill-row">
                                    {DIAGRAM_LOCATIONS.map(loc => {
                                        const active = selectedDiagramLocations.has(loc.value);
                                        return (
                                            <button
                                                key={loc.value}
                                                type="button"
                                                className={`diagram-pill${active ? ' diagram-pill-active' : ''}`}
                                                onClick={() => toggleDiagramLocation(loc.value)}
                                            >
                                                {loc.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Admin-only quality flags — bumped here to keep the main
                                filter row uncluttered. */}
                            {isAdmin && (
                                <div className="advanced-filter-group">
                                    <span className="advanced-filter-label">Quality:</span>
                                    <select
                                        value={filters.enrichStatus}
                                        onChange={e => setFilter('enrichStatus', e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">All Enrich</option>
                                        {Object.entries(ENRICH_STATUS_LABELS).map(([val, label]) => (
                                            <option key={val} value={val}>{label}</option>
                                        ))}
                                    </select>
                                    <MultiSelectFilter
                                        label="All Readiness"
                                        size="compact"
                                        options={Object.entries(READINESS_LABELS).map(([val, label]) => ({ value: val, label }))}
                                        selected={filters.readiness ? filters.readiness.split(',') : []}
                                        onChange={vals => setFilter('readiness', vals.join(','))}
                                        exclusive={REJECTED_EXCLUSIVE}
                                    />
                                </div>
                            )}

                            <div className="advanced-filter-group">
                                <span className="advanced-filter-label">Date added:</span>
                                <DatePicker
                                    value={filters.dateAddedFrom}
                                    max={filters.dateAddedTo || undefined}
                                    onChange={v => setFilter('dateAddedFrom', v)}
                                    style={{ width: 150 }}
                                    ariaLabel="Date added from"
                                    title="Added on or after"
                                />
                                <span className="advanced-filter-label">to</span>
                                <DatePicker
                                    value={filters.dateAddedTo}
                                    min={filters.dateAddedFrom || undefined}
                                    onChange={v => setFilter('dateAddedTo', v)}
                                    style={{ width: 150 }}
                                    ariaLabel="Date added to"
                                    title="Added on or before"
                                />
                            </div>

                            <div className="advanced-filter-group">
                                <span className="advanced-filter-label">Date modified:</span>
                                <DatePicker
                                    value={filters.dateModifiedFrom}
                                    max={filters.dateModifiedTo || undefined}
                                    onChange={v => setFilter('dateModifiedFrom', v)}
                                    style={{ width: 150 }}
                                    ariaLabel="Date modified from"
                                    title="Modified on or after"
                                />
                                <span className="advanced-filter-label">to</span>
                                <DatePicker
                                    value={filters.dateModifiedTo}
                                    min={filters.dateModifiedFrom || undefined}
                                    onChange={v => setFilter('dateModifiedTo', v)}
                                    style={{ width: 150 }}
                                    ariaLabel="Date modified to"
                                    title="Modified on or before"
                                />
                            </div>

                            <div className="advanced-filter-group">
                                <span className="advanced-filter-label">Appeared in exam:</span>
                                <MultiSelectFilter
                                    label="Any Exam"
                                    size="compact"
                                    options={PROGRAMME_OPTIONS.map(exam => ({ value: exam, label: exam }))}
                                    selected={filters.appearedInExam ? filters.appearedInExam.split(',').filter(Boolean) : []}
                                    onChange={vals => setFilter('appearedInExam', vals.join(','))}
                                />
                                <input
                                    type="number"
                                    placeholder="Year (e.g. 2024)"
                                    value={filters.appearedInYear}
                                    min={1990}
                                    max={2100}
                                    onChange={e => setFilter('appearedInYear', e.target.value.replace(/\D/g, '').slice(0, 4))}
                                    className="filter-select"
                                    style={{ width: 140 }}
                                />
                                <DatePicker
                                    value={filters.appearedOnDate}
                                    onChange={v => setFilter('appearedOnDate', v)}
                                    ariaLabel="Appeared on date"
                                    style={{ width: 160 }}
                                />
                                <input
                                    type="text"
                                    placeholder="Session (e.g. Shift 1)"
                                    value={filters.appearedInSession}
                                    onChange={e => setFilter('appearedInSession', e.target.value)}
                                    className="filter-select"
                                    style={{ width: 160 }}
                                />
                            </div>
                        </div>
                    )}
                </div>
                )}

                {/* Search row */}
                <div className="filter-row">
                    <input
                        type="text"
                        placeholder="Search questions..."
                        value={filters.search}
                        onChange={e => setFilter('search', e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleFetch()}
                        className="filter-search"
                        data-testid="question-search"
                    />
                    <button
                        className="btn-fetch"
                        onClick={handleFetch}
                        disabled={!isDirty && !isFetching}
                    >
                        {isFetching ? 'Fetching...' : 'Fetch'}
                    </button>
                </div>
            </div>

            {/* Tag pills for client-side filtering */}
            {allTagNames.length > 0 && (
                <div className="qtag-filter-pills" style={{ padding: '4px 8px' }}>
                    {allTagNames.map(tag => {
                        const active = selectedTags.has(tag);
                        const count = tagQuestionSets.get(tag)?.size ?? 0;
                        return (
                            <button
                                key={tag}
                                type="button"
                                className={`qtag-filter-pill${active ? ' qtag-filter-pill-active' : ''}`}
                                onClick={() => setSelectedTags(prev => {
                                    const next = new Set(prev);
                                    if (next.has(tag)) next.delete(tag); else next.add(tag);
                                    return next;
                                })}
                            >
                                {tag} <span className="qtag-filter-pill-count">{count}</span>
                            </button>
                        );
                    })}
                    {selectedTags.size > 0 && (
                        <button
                            type="button"
                            className="qtag-filter-clear"
                            onClick={() => setSelectedTags(new Set())}
                        >
                            Clear tags
                        </button>
                    )}
                </div>
            )}

            {/* Selection Bar */}
            {renderSelectionBar && filteredQuestions.length > 0 && (
                <div className="selection-bar">
                    <label className="select-all-label">
                        <input
                            type="checkbox"
                            checked={selectedIds.size === filteredQuestions.length && filteredQuestions.length > 0}
                            onChange={toggleSelectAll}
                        />
                        {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
                    </label>
                    {selectedIds.size > 0 && renderSelectionBar(selectedIds, clearSelection)}
                </div>
            )}

            {/* Question List */}
            <div className="question-list">
                {isLoading ? (
                    <div className="empty-list"><p>Loading...</p></div>
                ) : filteredQuestions.length === 0 ? (
                    <div className="empty-list"><p>No questions match your filters</p></div>
                ) : (
                    filteredQuestions.map((question, idx) => (
                        <div
                            key={question.id}
                            className={`question-list-item ${selectedQuestionId === question.id ? 'selected' : ''}`}
                            onClick={() => onSelectQuestion(question.id)}
                            data-testid="question-row"
                        >
                            <div className="list-item-header">
                                {renderSelectionBar && (
                                    <input
                                        type="checkbox"
                                        className="list-item-checkbox"
                                        checked={selectedIds.has(question.id)}
                                        onChange={e => { e.stopPropagation(); toggleSelectQuestion(question.id); }}
                                        onClick={e => e.stopPropagation()}
                                    />
                                )}
                                <span className="list-item-index">{idx + 1}</span>
                                {isAdmin ? (
                                    <span
                                        className="list-item-difficulty"
                                        style={{ backgroundColor: getReadinessColor(question.questionReadiness) }}
                                        title={getReadinessLabel(question.questionReadiness)}
                                    />
                                ) : (
                                    <span
                                        className="list-item-difficulty"
                                        style={{ backgroundColor: getDifficultyColor(question.difficulty) }}
                                        title={question.difficulty}
                                    />
                                )}
                                {question.module?.id && modulesById[question.module.id] && (
                                    <span className="list-item-module">{modulesById[question.module.id].displayName}</span>
                                )}
                                {question.module?.id && !modulesById[question.module.id] && question.module.name && (
                                    <span className="list-item-module">{question.module.name}</span>
                                )}
                                {question.questionType && (
                                    <span className="list-item-type">{QUESTION_TYPE_LABELS[question.questionType as QuestionType] ?? question.questionType}</span>
                                )}
                                {getExamBadgeText(question) && (
                                    <span className="list-item-exam">{getExamBadgeText(question)}</span>
                                )}
                                {question.importStatus === 'draft' && (
                                    <span className="list-item-draft">Draft</span>
                                )}
                                {questionTagNames.get(question.id)?.map(tag => (
                                    <span key={tag} className="qtag-inline">{tag}</span>
                                ))}
                            </div>
                            <div className="list-item-text">
                                <LatexText text={buildQuestionDisplayText(question)} truncate={120} />
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
